import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { Select, MenuItem, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';


const columns = [
    { field: 'Location', headerName: 'Location', width: 150, editable: true },
    { field: 'Amount as per IHR', headerName: 'Amount as per IHR', width: 130, editable: true },
    { field: 'Approved Amount', headerName: 'Approved Amount', type: 'number', width: 130, editable: true },
    { field: 'TSOI Adjustment', headerName: 'TSOI Adjustment', type: 'number', width: 150, editable: true },
    { field: 'Total approved amount (after adjusting TSOI)', headerName: 'Total approved amount (after adjusting TSOI)', type: 'number', width: 300, editable: true },
    { field: 'Disallowance', headerName: 'Disallowance', type: 'number', width: 110, editable: true },
    { field: 'Total Approved Amt', headerName: 'Total Approved Amt', type: 'number', width: 160, editable: true },
    { field: 'Final Rejection', headerName: 'Final Rejection', type: 'number', width: 110, editable: true },
];

const breakup_columns = [
    {
        field: 'Location',
        headerName: 'Location',
        width: 100,
        editable: true,
        color: 'blue'
    },
    {
        field: 'Wrong Rate',
        headerName: 'Wrong Rate',
        width: 100,
        editable: true,
        color: 'blue'
    },
    {
        field: 'Free Goods',
        headerName: 'Free Goods',
        width: 100,
        editable: true,
    },
    {
        field: 'Wrong channel',
        headerName: 'Wrong channel',
        type: 'number',
        width: 130,
        editable: true,
    },
    {
        field: 'Sales Return - current month',
        headerName: 'Sales Return - current month',
        type: 'number',
        width: 180,
        editable: true,
    }, {
        field: 'Sales return previous month',
        headerName: 'Sales return previous month',
        type: 'number',
        width: 180,
        editable: true,
    },
    {
        field: 'CN AR Adjustment',
        headerName: 'CN AR Adjustment',
        type: 'number',
        width: 150,
        editable: true,
    },
    {
        field: 'CN SRN',
        headerName: 'CN SRN',
        type: 'number',
        width: 100,
        editable: true,
    },
    {
        field: 'Other CN Disallowance',
        headerName: 'Other CN Disallowance',
        type: 'number',
        width: 170,
        editable: true,
    },
    {
        field: 'Cash Claims',
        headerName: 'Cash Claims',
        type: 'number',
        width: 110,
        editable: true,
    },
    {
        field: 'UNTAGGED SRN RECOVERY',
        headerName: 'UNTAGGED SRN RECOVERY',
        type: 'number',
        width: 160,
        editable: true,
    },

];

const COLORS = ['#090979', '#01bdf0','#fc466b'];

export default function DataGridWithChart({ data, type }) {

const [selectedLocation, setSelectedLocation] = React.useState('ALL'); // State for selected location

// Function to sum data for all locations
const sumDataForAllLocations = () => {
    return data.reduce(
        (acc, item) => {
            acc['Approved Amount'] += item['Approved Amount'] || 0;
            acc.Disallowance += item.Disallowance || 0;
            acc['Amount as per IHR'] += item['Amount as per IHR'] || 0;

            // Summing breakup columns
            breakup_columns.slice(1).forEach((col) => {
                acc[col.field] += item[col.field] || 0;
            });

            return acc;
        },
        {
            'Approved Amount': 0,
            Disallowance: 0,
            'Amount as per IHR': 0,
            ...Object.fromEntries(breakup_columns.slice(1).map((col) => [col.field, 0])),
        }
    );
};

// Filter data by selected location
const filteredData = selectedLocation && selectedLocation !== 'ALL'
    ? data.find((item) => item.Location === selectedLocation)
    : data;

// If 'ALL' is selected, sum the data
const chartData = selectedLocation === 'ALL'
    ? [
          { name: 'Approved Amount', value: sumDataForAllLocations()['Approved Amount'] },
          { name: 'Disallowance', value: sumDataForAllLocations().Disallowance },
        //   { name: 'Amount as per IHR', value: sumDataForAllLocations()['Amount as per IHR'] },
      ]
    : filteredData
    ? [
          { name: 'Approved Amount', value: filteredData['Approved Amount'] },
          { name: 'Disallowance', value: filteredData.Disallowance },
        //   { name: 'Amount as per IHR', value: filteredData['Amount as per IHR'] },
      ]
    : [];

const barchartData = selectedLocation === 'ALL'
    ? breakup_columns.slice(1).map((col) => ({
          name: col.headerName,
          value: sumDataForAllLocations()[col.field],
      }))
    : filteredData
    ? breakup_columns.slice(1).map((col) => ({
          name: col.headerName,
          value: filteredData[col.field],
      }))
    : [];

    const locationOptions = ['ALL', ...new Set(data.map((item) => item.Location))];
    const CustomLabel = ({ value }) => {
        return value.toLocaleString(); // Adds commas to large numbers
    };

    const CustomXAxisTick = ({ x, y, payload }) => {
        const text = payload.value;
        const maxLength = 10; // Set max length for each line
        const wrappedText = text.length > maxLength ? text.match(new RegExp('.{1,' + maxLength + '}', 'g')) : [text];
    
        return (
            <g transform={`translate(${x},${y + 10})`}>
                {wrappedText.map((line, index) => (
                    <text key={index} x={0} y={index * 12} textAnchor="middle" fontSize={10}>
                        {line}
                    </text>
                ))}
            </g>
        );
    };
    

    return (
        <Box sx={{ height: 200, width: '100%' }}>
            {/* Location Filter */}
        
            <h3>Site Wise Status</h3>
            <DataGrid
                rows={data}
                columns={columns}
                initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                    
                }}
                pageSizeOptions={[5, 10, 20]}
                sx={{
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#2185D0',
                        color: 'white',
                    },
                }}
                disableRowSelectionOnClick
            />  
            <br></br><br></br>
            <h3>Breakup Status</h3>
            <DataGrid
                rows={data}
                columns={breakup_columns}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[5, 10, 20]}
                sx={{
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#2185D0',
                        color: 'white',
                    },
                }}
                disableRowSelectionOnClick
            />  
        </Box>
    );
}
